"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { Client } from "@/lib/types/database";
import { useConnections } from "@/lib/contexts/ConnectionsContext";
import { useTickets } from "@/lib/hooks/api/tickets";
import { convertToTicketSummaryArray } from "@/lib/utils/crm/ticket-helpers";
import { EmailTemplate } from "@/lib/hooks/api/emails/email-templates";
import { useAutoSignatureSelector } from "@/lib/hooks/compose/auto-signature-selector";
import {
  mapContactToTemplateVariables,
  replaceTemplateVariables,
  extractTemplateVariables,
} from "@/lib/utils/crm/template-variables";
import { useVariableContext } from "@/components/crm/messages/contexts/VariableContext";
import { getEmailConnections } from "@/lib/utils/crm/email-connections";
import { useComposeFormState } from "@/lib/hooks/compose/useComposeFormState";
import { useComposeClientState } from "@/lib/hooks/compose/useComposeClientState";
import { useComposeRecipientsState } from "@/lib/hooks/compose/useComposeRecipientsState";
import { useComposeEmailFormat } from "@/lib/hooks/compose/useComposeEmailFormat";
import { ComposeFormValues } from "@/lib/types/compose-modal-types";
import { useComposePersonalization } from "./personalization";
import type { UseComposeProps } from "./types";

export const COMPOSE_LAST_FROM_ACCOUNT_KEY = "wtc-hub-compose-last-from-account";

/**
 * Refactored useCompose hook that orchestrates smaller focused hooks
 * Reduces prop drilling and improves performance by splitting concerns
 */
export function useCompose({
  isOpen = true,
  originalMessage,
  initialRecipient,
  initialClient,
  initialRecipients,
  initialBccRecipients,
  initialSubject,
  initialBody,
  selectedTicket: initialSelectedTicket,
  onSubmit,
}: UseComposeProps = {}) {
  const { connections } = useConnections();
  const { values: variableValues, hasUnfilledVariables: contextHasUnfilledVariables } =
    useVariableContext();

  // Use smaller focused hooks
  const formState = useComposeFormState({
    isOpen,
    initialSubject,
    initialBody,
  });

  const clientState = useComposeClientState({
    initialClient,
    toEmail: formState.form.watch("to")?.split(",")[0]?.trim(),
  });

  const recipientsState = useComposeRecipientsState({
    initialRecipients,
    initialBccRecipients,
  });

  const emailFormatState = useComposeEmailFormat();

  // Ticket management
  const { data: rawTickets = [], isLoading: ticketsLoading } = useTickets({
    limit: 50,
    clientId: clientState.selectedClient?.id,
  });

  const tickets = convertToTicketSummaryArray(rawTickets);

  // Email connections
  const emailConnections = useMemo(() => getEmailConnections(connections), [connections]);
  const defaultAccount = useMemo(() => emailConnections[0]?.connectionId || "", [emailConnections]);

  // Signature selection
  const selectedConnectionId = formState.form.watch("fromAccount");
  const { connectionSignatures, hasSignatures, hasDefaultSignature } = useAutoSignatureSelector({
    form: formState.form,
    isOpen,
    connectionId: selectedConnectionId,
  });

  // Unfilledvariables
  const unfilledVariables = useMemo(() => {
    const subject = formState.watchedSubject;
    const body = formState.watchedBody;
    return extractTemplateVariables(subject + " " + body);
  }, [formState.watchedSubject, formState.watchedBody]);

  const canSend = useMemo(() => {
    return (
      formState.form.formState.isValid &&
      recipientsState.recipients.length > 0 &&
      unfilledVariables.length === 0
    );
  }, [
    formState.form.formState.isValid,
    recipientsState.recipients.length,
    unfilledVariables.length,
  ]);

  // Refs for initialization tracking
  const hasAppliedLastFromAccount = useRef(false);

  // Apply last-selected from account
  useEffect(() => {
    if (!isOpen) {
      hasAppliedLastFromAccount.current = false;
      return;
    }
    if (hasAppliedLastFromAccount.current || !emailConnections.length) return;
    try {
      if (typeof window !== "undefined") {
        const saved = window.localStorage.getItem(COMPOSE_LAST_FROM_ACCOUNT_KEY);
        if (saved && emailConnections.some((c) => c.connectionId === saved)) {
          formState.form.setValue("fromAccount", saved);
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    hasAppliedLastFromAccount.current = true;
  }, [isOpen, emailConnections, formState.form]);

  // Handle template selection
  const handleTemplateSelect = useCallback(
    (template: EmailTemplate) => {
      let subject = template.subject;
      let body = template.body;

      if (clientState.selectedClient) {
        const variables = mapContactToTemplateVariables(clientState.selectedClient);
        subject = replaceTemplateVariables(subject, variables, false);
        body = replaceTemplateVariables(body, variables, true);
      }

      formState.form.setValue("subject", subject);
      formState.form.setValue("body", body);
    },
    [clientState.selectedClient, formState.form]
  );

  const { personalizationOrigin, handleClientSelect, handleVariableUpdate } =
    useComposePersonalization({
      isOpen,
      initialClient,
      form: formState.form,
      recipients: recipientsState.recipients,
      clientOptions: clientState.clientOptions,
      existingContact: clientState.existingContact,
      selectedClient: clientState.selectedClient,
      setSelectedClient: clientState.setSelectedClient,
    });

  // Handle submit
  const handleSubmit = useCallback(
    async (values: ComposeFormValues) => {
      if (onSubmit) {
        try {
          await onSubmit({
            ...values,
            selectedClient: clientState.selectedClient,
            selectedTicket: tickets[0] || null,
          });
        } catch (error) {
          console.error("Error submitting compose form:", error);
        }
      }
    },
    [onSubmit, clientState.selectedClient, tickets]
  );

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      formState.form.reset();
      recipientsState.setRecipients([]);
      recipientsState.setCcRecipients([]);
      recipientsState.setBccRecipients([]);
      recipientsState.setCcBccMode("cc");
      clientState.setSelectedClient(null);
    }
  }, [isOpen, formState.form, recipientsState, clientState]);

  return {
    // Form
    form: formState.form,
    watchedSubject: formState.watchedSubject,
    watchedBody: formState.watchedBody,

    // State
    selectedClient: clientState.selectedClient,
    selectedTicket: tickets[0] || null,
    clientSearchTerm: clientState.clientSearchTerm,
    recipients: recipientsState.recipients,
    ccBccMode: recipientsState.ccBccMode,
    setCcBccMode: recipientsState.setCcBccMode,
    ccRecipients: recipientsState.ccRecipients,
    setCcRecipients: recipientsState.setCcRecipients,
    bccRecipients: recipientsState.bccRecipients,
    setBccRecipients: recipientsState.setBccRecipients,

    // Modal-specific state
    activeTab: formState.activeTab,
    setActiveTab: formState.setActiveTab,
    showVariablePreview: formState.showVariablePreview,
    setShowVariablePreview: formState.setShowVariablePreview,

    // Computed
    emailConnections,
    clientOptions: clientState.clientOptions,
    filteredHubUsers: clientState.filteredHubUsers,
    tickets,
    unfilledVariables,
    canSend,

    // Loading states
    initialClientsLoading: clientState.clientsLoading,
    isSearching: clientState.isSearching,
    hubUsersLoading: clientState.hubUsersLoading,
    ticketsLoading,
    isSending: false,

    // Additional properties for compatibility
    variableValues,
    hasUnfilledVariables: unfilledVariables.length > 0,
    clientsLoading: clientState.clientsLoading,
    existingContact: clientState.existingContact,
    personalizationOrigin,

    // Signature-related properties
    connectionSignatures,
    hasSignatures,
    hasDefaultSignature,

    // Handlers
    setClientSearchTerm: clientState.setClientSearchTerm,
    setRecipients: recipientsState.setRecipients,
    handleTemplateSelect,
    handleClientSelect,
    handleVariableUpdate,
    handleSubmit,
  };
}
